/**
 * Built-in synthetic demo loop (SPEC §8.3): rolling hills + one 2 km climb, generated in code (works offline).
 * Placed in the Emmental so the swisstopo minimap shows real terrain around it.
 */
import { buildRoute } from './pipeline';
import { resample, type PlanarPoint } from './geometry';
import { lv95ToWgs84 } from './lv95';
import type { ProcessedRoute } from './model';

export const DEMO_ROUTE_ID = 'demo-emmental';
const E0 = 2_626_500;
const N0 = 1_198_500;

export const DEMO_CLIMB = { startFrac: 0.3, lengthM: 2000, gainM: 125, descentM: 4500 };

/** Periodic elevation profile over the loop length L. */
export function demoElevation(s: number, L: number): number {
  const tau = Math.PI * 2;
  let h = 720 + 8 * Math.sin((tau * 9 * s) / L) + 4 * Math.sin((tau * 22 * s) / L + 1);
  const c0 = DEMO_CLIMB.startFrac * L;
  const c1 = c0 + DEMO_CLIMB.lengthM;
  const c2 = c1 + DEMO_CLIMB.descentM;
  if (s >= c0 && s < c1)
    h += (DEMO_CLIMB.gainM * (1 - Math.cos((Math.PI * (s - c0)) / DEMO_CLIMB.lengthM))) / 2;
  else if (s >= c1 && s < c2)
    h += (DEMO_CLIMB.gainM * (1 + Math.cos((Math.PI * (s - c1)) / DEMO_CLIMB.descentM))) / 2;
  return h;
}

let cached: ProcessedRoute | null = null;

export function demoRoute(): ProcessedRoute {
  if (cached) return cached;
  const pts: PlanarPoint[] = [];
  const R = 1850;
  for (let i = 0; i < 1800; i++) {
    const th = (i / 1800) * Math.PI * 2;
    const r = R * (1 + 0.18 * Math.sin(3 * th + 0.4) + 0.07 * Math.cos(5 * th));
    pts.push({ e: E0 + r * Math.cos(th), n: N0 + 0.75 * r * Math.sin(th) });
  }
  const rs = resample(pts, 10, true);
  const ele = rs.d.map((s) => demoElevation(s, rs.total));
  const at = (frac: number) => {
    const i = Math.floor(frac * rs.d.length) % rs.d.length;
    return lv95ToWgs84(rs.e[i]!, rs.n[i]!);
  };
  const c1 = (DEMO_CLIMB.startFrac * rs.total + DEMO_CLIMB.lengthM) / rs.total;
  cached = buildRoute({
    id: DEMO_ROUTE_ID,
    name: 'Demo-Rundkurs Emmental',
    d: rs.d,
    e: rs.e,
    n: rs.n,
    ele,
    step: rs.step,
    total: rs.total,
    loop: true,
    waypoints: [
      { name: 'Chäsi', ...at(0.12) },
      { name: 'Passhöhe', ...at(c1) },
      { name: 'Aussichtspunkt', ...at(0.78) },
    ],
    source: 'synthetic',
    builtin: true,
  });
  cached.meta.createdAt = 0;
  cached.updatedAt = 0;
  return cached;
}
